/**
 * NewsFilterBar — sticky filter strip atop the feed (topic / source / tag).
 *
 * Day-1 implementation: link-based pivots to /news/topic/[t], /news/source/[s],
 * /news/tag/[t]. No multi-select chip combo Day-1 (single-axis filtering).
 *
 * Source: F-FE-HOTNEWS §3 + design source §2.4 NewsFilterBar.
 */

import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../../use-lang';
import { resolveHotNews, type PlayfulTag } from '../../i18n/hotnews';

const TOPICS = [
  'lao_dong',
  'thue',
  'visa',
  'dat_dai',
  'hon_nhan',
  'hinh_su',
  'kinh_doanh',
  'hanh_chinh',
] as const;

const TAGS: PlayfulTag[] = [
  'vua_ban_hanh',
  'sap_hieu_luc',
  'dang_hot',
  'drama_phap_ly',
  'trending_tuan',
  'cong_dan_can_biet',
];

type Topic = (typeof TOPICS)[number];

const SOURCES = [
  { id: 'chinhphu', name_vi: 'Cổng TTĐT Chính phủ', name_en: 'Government Portal' },
  { id: 'vbpl_news', name_vi: 'Bộ Tư pháp', name_en: 'Ministry of Justice' },
  { id: 'baophapluat', name_vi: 'Báo Pháp luật VN', name_en: 'Vietnam Law Newspaper' },
  { id: 'vnexpress', name_vi: 'VnExpress', name_en: 'VnExpress' },
];

export function NewsFilterBar() {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);
  const location = useLocation();
  const isFiltered = location.pathname !== '/news';

  return (
    <nav aria-label={copy.filter_bar_prompt} style={barStyle} className="news-filter-bar">
      <style>{`.news-filter-bar [data-row]::-webkit-scrollbar { display: none; }`}</style>
      <div style={rowStyle} data-row>
        <span style={labelStyle}>{copy.filter_topic_label}</span>
        {TOPICS.map((t) => (
          <FilterPill
            key={t}
            to={`/news/topic/${t}`}
            label={topicLabel(t, copy)}
            active={location.pathname === `/news/topic/${t}`}
          />
        ))}
      </div>
      <div style={rowStyle} data-row>
        <span style={labelStyle}>{copy.filter_tag_label}</span>
        {TAGS.map((t) => (
          <FilterPill
            key={t}
            to={`/news/tag/${t}`}
            label={(copy as Record<string, string>)[`tag_${t}`] ?? t}
            active={location.pathname === `/news/tag/${t}`}
          />
        ))}
      </div>
      <div style={rowStyle} data-row>
        <span style={labelStyle}>{copy.filter_source_label}</span>
        {SOURCES.map((s) => (
          <FilterPill
            key={s.id}
            to={`/news/source/${s.id}`}
            label={lang === 'en' ? s.name_en : s.name_vi}
            active={location.pathname === `/news/source/${s.id}`}
          />
        ))}
        {isFiltered ? (
          <Link
            to="/news"
            style={{
              ...pillStyle,
              border: '1px dashed var(--asklaw-border-strong)',
              fontWeight: 500,
            }}
          >
            {copy.filter_clear}
          </Link>
        ) : null}
      </div>
    </nav>
  );
}

function FilterPill({ to, label, active }: { to: string; label: string; active: boolean }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      style={{
        ...pillStyle,
        background: active ? 'var(--asklaw-ink)' : 'var(--asklaw-paper)',
        color: active ? 'var(--asklaw-cream)' : 'var(--asklaw-ink-soft)',
        borderColor: active ? 'var(--asklaw-ink)' : 'var(--asklaw-border)',
      }}
    >
      {label}
    </Link>
  );
}

function topicLabel(t: Topic, copy: ReturnType<typeof resolveHotNews>): string {
  const key = `digest_topic_${t}` as keyof typeof copy;
  return (copy as Record<string, string>)[key] ?? t;
}

const barStyle: React.CSSProperties = {
  position: 'sticky',
  top: 56,
  zIndex: 10,
  background: 'var(--asklaw-cream)',
  padding: '12px 0',
  marginBottom: 16,
  borderBottom: '1px solid var(--asklaw-border)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 6,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--asklaw-muted)',
  marginRight: 4,
  flexShrink: 0,
};

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 999,
  border: '1px solid var(--asklaw-border)',
  textDecoration: 'none',
  fontFamily: 'var(--font-sans)',
  flexShrink: 0,
};
